import { createSelector } from 'reselect';
import {
  getApplicant,
  isApplicantMarried,
  getApplicantLivesWithSecondParent,
  getApplicantMarriedOrLivingTogether,
  getApplicantMaritalStatus,
} from './applicant';
import { SEX } from '../components/shared/PersonGender/PersonGender';
import { getValue } from '../constants/getFieldValues';
import {
  getPassportDueDate,
  getPassportStatus,
  isRussianPassport,
} from '../validators/common';
import { validators } from '../validators';
import { ApplicantFields } from '../models/applicant';
import { ChildFields } from '../models/child';
import { PersonFields } from '../models/person';
import { getNotHiddenChildren } from './children';
import { ADDRESS_MATCH } from '../constants/spouse';

function getSpouseState(state) {
  return state.spouse;
}

export const getSpouse = createSelector(
  getApplicant,
  getSpouseState,
  function (applicant, spouse) {
    if (!applicant.sex.value) return spouse;
    const spouseSex = Object.assign({}, spouse.sex);
    spouseSex.disabled = true;
    spouseSex.value = applicant.sex.value === SEX.MALE ? SEX.FEMALE : SEX.MALE;
    return Object.assign({}, spouse, { sex: spouseSex });
  }
);

export const getSpouseAddressMatchWithApplicant = createSelector(
  getSpouse,
  ({ addressMatchWithApplicant }) => getValue(addressMatchWithApplicant)
);

export const isSpouseAddressMatchWithApplicant = createSelector(
  getSpouseAddressMatchWithApplicant,
  (value) => value === ADDRESS_MATCH.match
);

export const getSpouseAddress = createSelector(
  getSpouse,
  ({ address }) => address
);

export const isSpouseRegisteredInMoscow = createSelector(
  getSpouse,
  ({ isRegisteredInMoscow }) => isRegisteredInMoscow
);

export const validatedSpouseStep = (state) =>
  validators.spouseStepCompleted(state);

export const getSpouseStepCompleted = createSelector(
  validatedSpouseStep,
  isSpouseAddressMatchWithApplicant,
  getSpouseAddress,
  isSpouseRegisteredInMoscow,
  (
    spouseStepIsValid,
    addressMatchWithApplicant,
    address,
    isRegisteredInMoscow
  ) =>
    spouseStepIsValid &&
    (addressMatchWithApplicant ||
      address.isAddressCompleted ||
      !isRegisteredInMoscow)
);

export const getSpouseName = createSelector(getSpouse, ({ name }) =>
  getValue(name)
);

export const getSpouseSurname = createSelector(getSpouse, ({ surname }) =>
  getValue(surname)
);

export const getSpousePatronimic = createSelector(getSpouse, ({ patronymic }) =>
  getValue(patronymic)
);

export const getSpouseBirthDate = createSelector(getSpouse, ({ birthdate }) =>
  getValue(birthdate)
);

export const getSpouseBirthDateError = createSelector(
  getSpouse,
  ({ birthdate }) => birthdate.error
);

export const getSpouseCitizenship = createSelector(
  getSpouse,
  ({ citizenship }) => getValue(citizenship)
);

export const getSpouseDocument = createSelector(getSpouse, ({ dul }) => dul);

export const getSpouseDocumentType = createSelector(
  getSpouseDocument,
  ({ type }) => getValue(type)
);

export const isSpouseDocumentIsRussianPassport = createSelector(
  getSpouseDocumentType,
  (type) => isRussianPassport(type)
);

export const getSpouseDocumentNumber = createSelector(
  getSpouseDocument,
  ({ number }) => getValue(number)
);

export const getSpouseDocumentIssuer = createSelector(
  getSpouseDocument,
  ({ issuer }) => getValue(issuer)
);
export const getSpouseDocumentIssueDate = createSelector(
  getSpouseDocument,
  ({ issueDate }) => getValue(issueDate)
);
export const getSpouseDocumentDivisionCode = createSelector(
  getSpouseDocument,
  ({ divisionCode }) => getValue(divisionCode)
);

export const shouldShowMarriageConfirmationPopover: (state: {
  applicant: ApplicantFields;
  children: ChildFields[];
  spouse: PersonFields;
}) => boolean = createSelector(
  isApplicantMarried,
  getApplicantLivesWithSecondParent,
  getSpouseStepCompleted,
  getNotHiddenChildren,
  (isMarried, doLiveWithSecondParent, isSpouseCompleted, children) => {
    return Boolean(
      (isMarried && (isSpouseCompleted || children.length)) ||
        (doLiveWithSecondParent && isSpouseCompleted)
    );
  }
);

export const spouseAddressFulFilled = createSelector(
  getSpouse,
  ({ addressMatchWithApplicant, address, isRegisteredInMoscow }) =>
    !isRegisteredInMoscow ||
    addressMatchWithApplicant.value === ADDRESS_MATCH.match ||
    address.isAddressCompleted
);
export const spouseAddressComplete = createSelector(
  getApplicantMarriedOrLivingTogether,
  spouseAddressFulFilled,
  getApplicantMaritalStatus,
  (
    doLiveWithSecondParent,
    spouseAddressFulFilled,
    applicantMaritalStatusFilled
  ) =>
    doLiveWithSecondParent
      ? spouseAddressFulFilled
      : applicantMaritalStatusFilled
);

export const validateSpousePassportStatus: (state: {
  spouse: PersonFields;
}) => string = createSelector(
  getSpouseBirthDate,
  getSpouseDocumentIssueDate,
  isSpouseDocumentIsRussianPassport,
  (birthDate, issueDate, isRussianPassport) =>
    getPassportStatus(birthDate, issueDate, isRussianPassport)
);

export const getSpousePassportStatus = createSelector(
  getSpouseDocument,
  ({ status }) => status
);

export const getSpousePassportDueDate = createSelector(
  getSpouseBirthDate,
  (birthdate) => getPassportDueDate(birthdate)
);
